import { Box, Flex, useBreakpointValue } from '@chakra-ui/react';
import {
  MdHome as RecipesIcon,
  MdAdd as AddIcon,
} from 'react-icons/md';
import { useLocation } from 'wouter';
import AiChefIcon from './AiChefIcon';
import { useAuth } from '@/hooks/useAuth';
import { useChatContext } from '@/hooks/useChatContext';
import { useScrollVisibility } from '@/hooks/useScrollVisibility';
import { useImportSheet } from '@/contexts/ImportSheetContext';
import { ROUTES } from '@/types';
import BottomNavButton from './BottomNavButton';

interface BottomNavBarProps {
  recipeId?: string;
  collectionId?: string;
}

export default function BottomNavBar({ recipeId, collectionId }: BottomNavBarProps) {
  const [location, navigate] = useLocation();
  const { isAuthenticated } = useAuth();
  const { openChat } = useChatContext();
  const isMobile = useBreakpointValue({ base: true, lg: false });
  const { isVisible } = useScrollVisibility({
    enabled: true,
    hideThreshold: 80,
    showThreshold: 10,
  });
  const { isOpen: isImportSheetOpen, open: openImportSheet } = useImportSheet();

  const isAIChefActive = location === '/ai-chef';
  const isBrowseActive = location === '/' || location.startsWith('/recipes') || location.startsWith('/recipe-groups');

  const handleAIChef = () => {
    openChat({ recipeId: recipeId ?? null, collectionId: collectionId ?? null });
  };

  if (!isMobile || !isAuthenticated) return null;

  return (
    <>
      <Box
        position="fixed"
        bottom={0}
        left={0}
        right={0}
        bg="bg.surface"
        borderTop="1px solid"
        borderColor="border.muted"
        zIndex={100}
        boxShadow="0 -1px 0 var(--chakra-colors-border-muted)"
        transform={isVisible ? 'translateY(0)' : 'translateY(100%)'}
        transition="transform 0.3s ease-in-out"
        pb="env(safe-area-inset-bottom)"
        overflow="visible"
      >
        {/* FAB icon — absolutely positioned, floats above the bar */}
        <Box
          position="absolute"
          top="-10px"
          left="50%"
          transform="translateX(-50%)"
          w="52px"
          h="52px"
          borderRadius="15px"
          bg={isAIChefActive ? 'accent.emphasisHover' : 'accent.emphasis'}
          display="flex"
          alignItems="center"
          justifyContent="center"
          cursor="pointer"
          onClick={handleAIChef}
          role="button"
          aria-label="AI Chef"
          transition="background 0.15s, transform 0.15s"
          _hover={{ bg: 'accent.emphasisHover', transform: 'scale(1.05)' }}
          zIndex={1}
        >
          <AiChefIcon />
        </Box>

        <Flex align="center" h="56px" px={1}>
          <BottomNavButton
            icon={RecipesIcon}
            label="Home"
            isActive={isBrowseActive}
            onClick={() => navigate(ROUTES.HOME())}
          />

          {/* Centre slot — tap area only, FAB icon floats above */}
          <Box flex={1} h="56px" cursor="pointer" onClick={handleAIChef} />

          <BottomNavButton
            icon={AddIcon}
            label="Add"
            ariaLabel="Add a recipe"
            isActive={isImportSheetOpen}
            onClick={() => openImportSheet()}
          />
        </Flex>
      </Box>
    </>
  );
}
